import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ClientOnly, createFileRoute } from '@tanstack/react-router'
import { parse } from 'best-effort-json-parser'
import {
  BriefcaseIcon,
  CodeIcon,
  DownloadIcon,
  Edit3Icon,
  EditIcon,
  EyeIcon,
  FileTextIcon,
  GraduationCapIcon,
  Loader2Icon,
  PaletteIcon,
  RefreshCcwIcon,
  UploadIcon,
} from 'lucide-react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'

import { useConfig } from '@/components/hooks/use-config'
import { useResumeUploader } from '@/components/resume-tailor/1-upload-stage'
import { ResumeDownloader } from '@/components/resume-tailor/resume-downloader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import {
  ResumeTemplate,
  templates as templateData,
} from '@/lib/resume-templates/templates'
import { downloadMarkdownFile, formatBytes } from '@/lib/utils'

// Lazy load Monaco editor to avoid SSR issues - it's client-only
const MarkdownEditor = React.lazy(() =>
  import('@/components/resume-tailor/markdown-editor').then((mod) => ({
    default: mod.MarkdownEditor,
  })),
)

export const Route = createFileRoute('/_index/templates')({
  component: () => (
    <ClientOnly>
      <RouteComponent />
    </ClientOnly>
  ),
})

const categoryIcons = {
  professional: BriefcaseIcon,
  creative: PaletteIcon,
  technical: CodeIcon,
  academic: GraduationCapIcon,
}

export default function RouteComponent() {
  const [templates, setTemplates] = React.useState<ResumeTemplate[]>(
    templateData || [],
  )
  const [filter, setFilter] = useState<string>('all')

  const filteredTemplates =
    filter === 'all'
      ? templates
      : templates.filter((t) => t.category === filter)

  const handleDownload = (content: string, name: string) => {
    downloadMarkdownFile({ filename: name, text: content })
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">
          Resume Templates
        </h1>
        <p className="text-muted-foreground">
          Convert your existing PDF Resume to professionally designed templates
          and customize them for your needs
        </p>
      </div>

      <div className="mb-8 flex justify-center">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all">All Templates</TabsTrigger>
            <TabsTrigger value="professional" className="gap-2">
              <BriefcaseIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Professional</span>
            </TabsTrigger>
            <TabsTrigger value="technical" className="gap-2">
              <CodeIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Technical</span>
            </TabsTrigger>
            <TabsTrigger value="creative" className="gap-2">
              <PaletteIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Creative</span>
            </TabsTrigger>
            <TabsTrigger value="academic" className="gap-2">
              <GraduationCapIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Academic</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {filteredTemplates.map((template) => {
          const Icon = categoryIcons[template.category]
          return (
            <Card key={template.id} hover>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription>{template.description}</CardDescription>
                    </div>
                  </div>
                  <Badge variant="secondary">{template.category}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4 h-32 overflow-hidden rounded-lg border bg-muted/30 p-4">
                  <div className="line-clamp-5 font-mono text-xs whitespace-pre-wrap text-muted-foreground">
                    {template.content?.slice(60, 300)}...
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-muted/80 to-transparent" />
                </div>

                <div className="flex gap-2">
                  <ResumeConverterDialog template={template} />
                  <PreviewDialog
                    template={template}
                    setTemplates={setTemplates}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      handleDownload(template.content || '', template.name)
                    }
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

const PreviewDialog = (props: {
  template: ResumeTemplate
  setTemplates: React.Dispatch<React.SetStateAction<ResumeTemplate[]>>
}) => {
  const { template, setTemplates } = props
  const [mode, setMode] = React.useState<'default' | 'edit'>('default')
  const [editText, setEditText] = React.useState('')

  return (
    <Dialog>
      <DialogTrigger
        render={(props) => (
          <Button
            {...props}
            variant="outline"
            className="flex-1 cursor-pointer gap-2"
          >
            <EyeIcon className="h-4 w-4" />
            Preview | <Edit3Icon className="h-3 w-3" /> Edit
          </Button>
        )}
      />
      <DialogContent className="flex h-[90vh] max-h-[90vh] min-w-[90vw] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>

          <div className="flex items-center justify-between">
            <DialogDescription>{template.description}</DialogDescription>
            <div className="mr-4 flex gap-2">
              {mode == 'edit' ? (
                <div className="flex w-full justify-end gap-2">
                  <Button onClick={() => setMode('default')} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      setMode('default')
                      setTemplates((prev) =>
                        prev.map((tm) =>
                          tm.id === template.id
                            ? { ...template, content: editText }
                            : tm,
                        ),
                      )
                    }}
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <div className="flex flex-row items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMode('edit')
                      setEditText(template?.content || '')
                    }}
                  >
                    <EditIcon className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      downloadMarkdownFile({
                        filename: template.name,
                        text: template.content || '',
                      })
                    }
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Download
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="flex h-full min-h-0 w-full min-w-0">
          <ScrollArea className="h-full max-h-full min-h-0 w-full max-w-full min-w-0">
            {mode === 'edit' ? (
              <React.Suspense
                fallback={
                  <div className="flex h-full items-center justify-center">
                    <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                }
              >
                <MarkdownEditor
                  text={editText || ''}
                  onTextChange={(text) => setEditText(text)}
                />
              </React.Suspense>
            ) : (
              <div className="overflow-hidden bg-muted p-8 sm:p-16">
                <div className="prose w-full max-w-full rounded-lg bg-white p-8 shadow-lg sm:p-16">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
                  >
                    {template.content}
                  </Markdown>
                </div>
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const ACCEPTED_FORMATS = { 'application/pdf': ['.pdf'] }
const ResumeConverterDialog = (props: { template: ResumeTemplate }) => {
  const { template } = props

  const { config } = useConfig()

  const [isStreaming, setIsStreaming] = React.useState<boolean>()
  const [uploadedFile, setUploadedFile] = React.useState<File>()

  const [convertedResume, setConvertedResume] = React.useState<string>()

  const convertMutation = useMutation({
    mutationFn: async (opts: { resume: File; template: string }) => {
      const formDataToSend = new FormData()
      formDataToSend.append('llm_settings', JSON.stringify(config))
      formDataToSend.append('resume', opts.resume)
      formDataToSend.append('template', opts.template)

      const res = await fetch('/api/convert', {
        method: 'POST',
        body: formDataToSend,
      })

      if (!res.ok) throw new Error('Failed to convert')

      const reader = res.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) throw new Error('No reader available')

      let accumulatedContent = ''
      let sseBuffer = ''

      while (true) {
        setIsStreaming(true)
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        sseBuffer += chunk

        // Parse SSE lines
        const lines = sseBuffer.split('\n')
        sseBuffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || trimmed === 'data: [DONE]') continue
          if (!trimmed.startsWith('data: ')) continue

          try {
            const jsonStr = trimmed.slice(6)
            const event = JSON.parse(jsonStr)

            if (event.type === 'content' && event.delta) {
              accumulatedContent += event.delta
            }
          } catch (e) {
            // Ignore parse errors for incomplete chunks
          }
        }

        // accumulatedContent = cleanChunk(accumulatedContent)

        // Parse accumulated content and extract resume field
        // Handle both JSON {resume: "..."} format and raw markdown
        try {
          const trimmedContent = accumulatedContent.trim()
          // Check if it looks like JSON (starts with {)
          if (trimmedContent.startsWith('{')) {
            const partialData = parse(accumulatedContent)
            if (partialData?.resume) {
              setConvertedResume(partialData.resume)
            }
          } else {
            // Raw markdown response
            setConvertedResume(accumulatedContent)
          }
        } catch (e) {
          // Fallback to raw content
          if (accumulatedContent.trim()) {
            setConvertedResume(accumulatedContent)
          }
        }
      }

      // Final parse - handle both JSON and raw markdown
      const trimmedFinal = accumulatedContent.trim()
      if (trimmedFinal.startsWith('{')) {
        try {
          const finalData = parse(accumulatedContent)
          return finalData?.resume || accumulatedContent
        } catch (e) {
          console.error('Failed to parse final JSON', accumulatedContent)
          return accumulatedContent
        }
      }
      return accumulatedContent
    },
    onSuccess: (data) => {
      setConvertedResume(data)
      setIsStreaming(false)
    },
  })

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return

      setUploadedFile(acceptedFiles[0])
      convertMutation.mutate({
        resume: acceptedFiles[0],
        template: props.template.content as string,
      })
    },
    [props.template],
  )

  const { getRootProps, getInputProps, isDragActive, MAX_FILE_SIZE } =
    useResumeUploader({ onDrop, accept: ACCEPTED_FORMATS })

  return (
    <Dialog>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <DialogTrigger
              render={(props) => (
                <Button {...props}>
                  <RefreshCcwIcon className="h-4 w-4" />
                  <span>Convert</span>
                </Button>
              )}
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>Convert your existing PDF to this markdown template.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="flex h-[90vh] max-h-[90vh] min-w-[90vw] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>

          <div className="flex flex-row items-center justify-between">
            <DialogDescription>{template.description}</DialogDescription>
            {convertedResume && (
              <div className="mr-6 flex flex-row items-center gap-2">
                {(convertMutation.isPending || isStreaming) && (
                  <Loader2Icon className="animate-spin" />
                )}
                <ResumeDownloader
                  filename={`${template.id}-personalized-resume`}
                  isStreaming={isStreaming}
                  resumeText={convertedResume || template.content || ''}
                />
                <Button
                  variant="outline"
                  disabled={convertMutation.isPending || isStreaming}
                  onClick={() => {
                    setConvertedResume(undefined)
                    setUploadedFile(undefined)
                  }}
                >
                  Reset
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>
        <div className="flex h-full min-h-0 w-full min-w-0">
          {!convertedResume && !isStreaming ? (
            <div className="flex w-full items-center justify-center">
              <div
                {...getRootProps()}
                className="flex h-2/3 w-2/3 cursor-pointer flex-col justify-center rounded-xl border-2 border-dashed px-4 py-2 text-center transition-colors hover:border-primary/50 hover:bg-muted/50"
              >
                <input {...getInputProps()} />
                <UploadIcon className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">
                  {isDragActive
                    ? 'Drop the resume here...'
                    : 'Click to upload or drag and drop'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {Object.values(ACCEPTED_FORMATS).join(', ')} only.{' '}
                  {`(Max Size: ${formatBytes(MAX_FILE_SIZE)})`}
                </p>
                {uploadedFile && (
                  <div className="my-4 flex flex-col">
                    <div className="mx-auto flex flex-row gap-1">
                      {convertMutation.isPending && (
                        <Loader2Icon className="animate-spin" />
                      )}
                      <FileTextIcon className="mb-2 h-6 w-6 text-muted-foreground" />
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {uploadedFile.name}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatBytes(uploadedFile.size)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <ScrollArea className="h-full max-h-full min-h-0 w-full max-w-full min-w-0">
              <div className="overflow-hidden bg-muted p-8 sm:p-16">
                <div className="prose w-full max-w-full rounded-lg bg-white p-8 shadow-lg sm:p-16">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
                  >
                    {convertedResume}
                  </Markdown>
                </div>
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
